import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { NavigationPill } from '@/components/ui/3d-adaptive-navigation-bar';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, Coins } from 'lucide-react';
import { useState, lazy, Suspense } from 'react';
import logo from '@/assets/logo.webp';

// Lazy: only loads when the user clicks "Como Funciona". Keeps Radix Dialog out of the initial bundle.
const VSLDialog = lazy(() => import('@/components/landing/VSLPlayer').then((m) => ({ default: m.VSLDialog })));

export function Navbar() {
  const { isAuthenticated, credits, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vslOpen, setVslOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation items for unauthenticated users
  const publicNavItems = [
    { label: 'Início', id: 'home', onClick: () => navigate('/') },
    { label: 'Como Funciona', id: 'how-it-works', onClick: () => setVslOpen(true) },
    { label: 'Entrar', id: 'auth', onClick: () => navigate('/auth') },
  ];

  // Navigation items for authenticated users
  const authNavItems = [
    { label: 'Dashboard', id: 'dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Gerar Prompt', id: 'generate', onClick: () => navigate('/generate') },
    { label: 'Histórico', id: 'history', onClick: () => navigate('/history') },
    { label: 'Biblioteca', id: 'library', onClick: () => navigate('/library') },
  ];

  // Determine active item based on current route
  const getActiveId = () => {
    if (!isAuthenticated) {
      if (location.pathname === '/auth') return 'auth';
      if (location.hash === '#como-funciona') return 'how-it-works';
      return 'home';
    } else {
      if (location.pathname === '/dashboard') return 'dashboard';
      if (location.pathname === '/generate') return 'generate';
      if (location.pathname === '/history') return 'history';
      if (location.pathname === '/library') return 'library';
      return 'dashboard';
    }
  };

  const navItems = isAuthenticated ? authNavItems : publicNavItems;

  return (
    <nav className="fixed left-0 right-0 z-50 liquid-glass border-b border-border/30" style={{ top: 'var(--banner-h, 0px)' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img src={logo} alt="PromptCodex" width={259} height={44} className="h-11 w-auto" decoding="async" fetchPriority="high" />
          </Link>

          {/* Desktop Navigation - 3D Pill */}
          <div className="hidden md:flex items-center gap-4">
            <NavigationPill
              items={navItems}
              activeId={getActiveId()}
              collapsedWidth={160}
              expandedWidth={isAuthenticated ? 520 : 480}
            />
            
            {!isAuthenticated ? (
              <Button variant="hero" size="sm" onClick={() => navigate('/auth?mode=register')}>
                Começar Agora
              </Button>
            ) : (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/50 border border-border">
                  <Coins className="w-4 h-4 text-primary" />
                  <span className="font-medium">{credits}</span>
                  <span className="text-muted-foreground text-sm">créditos</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  Sair
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-fade-in">
            <div className="flex flex-col gap-4">
              {!isAuthenticated ? (
                <>
                  <button 
                    className="text-muted-foreground hover:text-foreground transition-colors text-left"
                    onClick={() => { setVslOpen(true); setMobileMenuOpen(false); }}
                  >
                    Como Funciona
                  </button>
                  <Link 
                    to="/auth" 
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Entrar
                  </Link>
                  <Button variant="hero" onClick={() => { navigate('/auth?mode=register'); setMobileMenuOpen(false); }}>
                    Começar Agora
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/50 border border-border w-fit">
                    <Coins className="w-4 h-4 text-primary" />
                    <span className="font-medium">{credits}</span>
                    <span className="text-muted-foreground text-sm">créditos</span>
                  </div>
                  <Link 
                    to="/dashboard" 
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/generate" 
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Gerar Prompt
                  </Link>
                  <Link 
                    to="/history" 
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Histórico
                  </Link>
                  <Link 
                    to="/library" 
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Biblioteca
                  </Link>
                  <Button variant="ghost" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                    Sair
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {vslOpen && (
        <Suspense fallback={null}>
          <VSLDialog open={vslOpen} onOpenChange={setVslOpen} />
        </Suspense>
      )}
    </nav>
  );
}
